import { AlertTriangle, Hourglass, Loader2, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { BatchLaneData, VertexBatchLane } from '@/types/batch-lane';

// In-flight job age thresholds (minutes). Amber past one hour; red past the
// 3h escape-hatch threshold where ops would consider cancel-and-resync.
const JOB_AGE_AMBER_MINUTES = 60;
const JOB_AGE_RED_MINUTES = 180;

// eslint-disable-next-line react-refresh/only-export-components -- non-component export alongside a component (affects dev fast-refresh only); file split is out of scope for CUR-1330
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest > 0 ? `${hours}h ${rest}m` : `${hours}h`;
}

function minutesSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, (Date.now() - then) / 60_000);
}

function jobAgeClass(ageMinutes: number | null): string {
  if (ageMinutes === null) return 'text-muted-foreground';
  if (ageMinutes >= JOB_AGE_RED_MINUTES) return 'text-accent-red font-medium';
  if (ageMinutes >= JOB_AGE_AMBER_MINUTES) return 'text-accent-amber font-medium';
  return 'text-muted-foreground';
}

/**
 * Badge for active/queued task rows that are shielded by the Vertex Batch
 * lane (CUR-1094) — shown instead of the frozen stage progress bar so a
 * batch round-trip doesn't read as a stuck item.
 */
export function VertexBatchBadge({ lane }: { lane: VertexBatchLane }) {
  return (
    <Badge variant="warning" className="text-xs" data-testid="vertex-batch-badge">
      <Hourglass className="h-3 w-3 mr-1 inline" />
      Vertex Batch {lane === 'in_flight' ? '(job in flight)' : '(buffered)'}
    </Badge>
  );
}

interface BatchLaneTabProps {
  data: BatchLaneData | undefined;
  isLoading: boolean;
  isError?: boolean;
}

/**
 * "Batch" tab of the Task Queue Management modal (CUR-1094): Vertex Batch
 * buffer, in-flight jobs, awaiting-drip count, today's throughput, and a
 * dispatcher-stall warning. Read-only in v1 — the escape hatch stays CLI/ops.
 */
export function BatchLaneTab({ data, isLoading, isError }: BatchLaneTabProps) {
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Batch lane status unavailable
      </div>
    );
  }

  const bufferAge = minutesSince(data.buffer.oldest_enqueued_at);

  return (
    <div className="space-y-4">
      {/* Dispatcher stall warning — the silent-flush-no-op failure mode */}
      {data.dispatcher.stalled && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-3"
          data-testid="dispatcher-stalled-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">
              Batch dispatcher may be stalled
            </p>
            <p className="text-xs text-amber-700 mt-1">
              {data.buffer.depth} item{data.buffer.depth === 1 ? '' : 's'} waiting in the buffer
              (oldest {formatMinutes(bufferAge)}) but no Vertex job has been submitted in the last{' '}
              {data.dispatcher.stall_window_minutes} minutes. Check the flush task and the
              VERTEX_BATCH_* env vars on the utility worker.
            </p>
          </div>
        </div>
      )}

      {data.call_lane.stalled && (
        <div
          className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-3"
          data-testid="call-poller-stalled-banner"
        >
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Batch results are not being collected</p>
            <p className="text-xs text-red-700 mt-1">
              {data.call_lane.open_jobs} open call job{data.call_lane.open_jobs === 1 ? '' : 's'} (
              {data.call_lane.open_records} requests), oldest not polled for{' '}
              {formatMinutes(data.call_lane.oldest_unpolled_minutes)}. The poller runs every 2 minutes
              on the analysis worker; check its logs for expired poll_vertex_batch_call_jobs ticks.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        {(['live', 'batch'] as const).map((lane) => {
          const counts = data.lanes[lane];
          return (
            <Card key={lane} data-testid={`lane-${lane}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm capitalize">{lane} lane</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {counts.queued}
                  <span className="text-sm font-normal text-muted-foreground"> queued</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {counts.processing} processing • {counts.pending} pending
                  {lane === 'batch' && <> • {counts.awaiting_batch} awaiting Vertex</>}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Buffer + drip + today summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Package className="h-4 w-4" />
              Buffer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.buffer.depth}</div>
            <div className="text-xs text-muted-foreground">
              {data.buffer.depth > 0 ? (
                <>
                  oldest {formatMinutes(bufferAge)}
                  {(data.buffer.max_flush_attempt_count ?? 0) > 0 && (
                    <> • {data.buffer.max_flush_attempt_count} flush attempts</>
                  )}
                </>
              ) : (
                'awaiting next flush cycle'
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Today</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.today.success_count}
              <span className="text-sm font-normal text-muted-foreground">
                {' '}
                / {data.today.records} records ok
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {data.today.jobs} jobs • {data.today.batch_transcripts} transcripts persisted
              {data.today.failure_count > 0 && (
                <span className="text-accent-red"> • {data.today.failure_count} failed</span>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Call jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.call_lane.open_jobs}
              <span className="text-sm font-normal text-muted-foreground"> open</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {data.call_lane.open_jobs > 0 && (
                <>last polled {formatMinutes(data.call_lane.oldest_unpolled_minutes)} ago • </>
              )}
              {data.call_lane.today.jobs} today
              {data.call_lane.today.failure_count > 0 && (
                <span className="text-accent-red"> • {data.call_lane.today.failure_count} failed</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* In-flight Vertex jobs */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            In-flight Vertex jobs ({data.jobs_in_flight.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.jobs_in_flight.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">No jobs in flight</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Job</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Records</th>
                    <th className="py-2 pr-4 font-medium">Submitted</th>
                    <th className="py-2 font-medium">Age</th>
                  </tr>
                </thead>
                <tbody>
                  {data.jobs_in_flight.map((job) => (
                    <tr key={job.job_name} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{job.job_id_short}</td>
                      <td className="py-2 pr-4">
                        <Badge variant="outline" className="text-xs">
                          {job.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">{job.record_count}</td>
                      <td className="py-2 pr-4 text-xs text-muted-foreground">
                        {job.submitted_at ? new Date(job.submitted_at).toLocaleTimeString() : '—'}
                      </td>
                      <td className={`py-2 text-xs ${jobAgeClass(job.age_minutes)}`}>
                        {formatMinutes(job.age_minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
